import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useLanguage } from '../core/hooks/use-language';
import { useSession } from '../core/hooks/use-session';
import { ensureSessionLoaded } from '../core/api/auth-service';

/**
 * Keeps signed-out visitors off the portal routes.
 *
 * This is presentation, not security. Every portal response is re-checked on the server against the
 * customer session and the caller's own records — a rule must never live only here.
 *
 * It waits for one confirmation from the server before deciding, which the token version did not
 * have to: an expiry inside a token could be read locally, and a session's end cannot. The wait
 * happens once per page load and only on portal routes.
 */
export function RequireSession() {
    const session = useSession();
    const language = useLanguage();
    const location = useLocation();

    const [checked, setChecked] = useState(false);

    useEffect(() => {
        let active = true;

        void ensureSessionLoaded().then(() => {
            if (active) {
                setChecked(true);
            }
        });

        return () => {
            active = false;
        };
    }, []);

    // Nothing rather than a spinner: with a cached account the shell around this renders normally
    // and the confirmation usually lands within a frame, so a spinner would flash for no reason.
    if (!checked) {
        return null;
    }

    if (session === null) {
        return (
            <Navigate
                to={`/${language}/account/sign-in`}
                state={{ from: location.pathname }}
                replace
            />
        );
    }

    return <Outlet />;
}
